import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/*
  Button variants (UI foundation, mockup kit):
  - primary / default  navy fill — the ONE main action of a screen
  - accent             lime fill, navy text — rare accents, never the main action on white
  - outline            white surface + hairline border — secondary actions
  - ghost              no chrome until hover — toolbars, icon buttons, row "⋯"
  - destructive/danger outline red (not filled) — delete/irreversible; confirm first
  - secondary          soft gray fill (legacy)
  - link               inline navy text link
  Sizes: xs 26px · sm 30px · default 36px · lg 44px · icon 36px · icon-sm 30px · icon-xs 28px
*/
const primaryClasses =
  "bg-brand text-brand-fg shadow-[0_1px_2px_rgb(25_25_112/0.25)] hover:bg-brand-hover"
const dangerClasses =
  "border border-[color:var(--danger-border)] bg-surface text-status-red-ink shadow-card hover:bg-status-red-bg"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[7px] whitespace-nowrap rounded-control text-[13.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: primaryClasses,
        primary: primaryClasses,
        accent:
          "bg-[color:var(--accent-fill)] text-[color:var(--accent-fill-fg)] shadow-sm hover:bg-[color:var(--accent-fill-hover)]",
        destructive: dangerClasses,
        danger: dangerClasses,
        outline:
          "border border-line bg-surface shadow-card hover:bg-surface-hover",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-surface-hover hover:text-ink",
        link: "text-brand-ink underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5",
        xs: "h-[26px] gap-[5px] rounded-[7px] px-2 text-xs [&_svg]:size-3.5",
        sm: "h-[30px] gap-1.5 rounded-lg px-2.5 text-[12.5px] [&_svg]:size-3.5",
        lg: "h-11 px-6 text-sm",
        icon: "h-9 w-9",
        "icon-sm": "h-[30px] w-[30px] rounded-lg",
        "icon-xs": "h-7 w-7 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
